import { useState, useEffect } from "react";
import type { MeetingRoom } from "@/types/calendar";
import type { AuthUser } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { RefreshCw, LogOut } from "lucide-react";

interface CalendarHeaderProps {
  rooms: MeetingRoom[];
  lastRefresh: Date | null;
  onRefresh: () => void;
  loading?: boolean;
  workingHoursOnly?: boolean;
  onToggleWorkingHours?: () => void;
  user?: AuthUser;
  onSignOut?: () => void;
}

function getRelativeTime(date: Date, now: Date): string {
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}초 전`;
  const diffMin = Math.floor(diffSec / 60);
  return `${diffMin}분 전`;
}

export function CalendarHeader({
  rooms,
  lastRefresh,
  onRefresh,
  loading,
  workingHoursOnly,
  onToggleWorkingHours,
  user,
  onSignOut,
}: CalendarHeaderProps) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const dateStr = now.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });

  return (
    <div className="sticky top-0 z-20 bg-background border-b">
      {/* Title row */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div>
          <h1 className="text-lg md:text-xl font-semibold">
            Brandazine 회의실 예약 현황
          </h1>
          <p className="text-sm text-muted-foreground">{dateStr}</p>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1.5">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              {lastRefresh.toLocaleTimeString("ko-KR")}
              <span className="opacity-70">
                ({getRelativeTime(lastRefresh, now)})
              </span>
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {onToggleWorkingHours && (
            <button
              onClick={onToggleWorkingHours}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                workingHoursOnly
                  ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                  : "bg-transparent text-gray-400 border-gray-300 dark:border-gray-600"
              }`}
              title={
                workingHoursOnly ? "20:30에 화면 자동 꺼짐" : "화면 항상 켜짐"
              }
            >
              {workingHoursOnly ? "자동 꺼짐 ON" : "자동 꺼짐 OFF"}
            </button>
          )}
          {user && (
            <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-border">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-6 h-6 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                  {(user.name || user.email)[0]?.toUpperCase()}
                </div>
              )}
              <span className="text-xs text-muted-foreground hidden md:inline max-w-[140px] truncate">
                {user.name || user.email}
              </span>
              {onSignOut && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSignOut}
                  title="로그아웃"
                  className="h-7 w-7 p-0"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Room columns header */}
      <div className="flex">
        {/* Time column spacer */}
        <div className="w-16 md:w-20 shrink-0" />

        {/* Room name columns */}
        {rooms.map((room, index) => (
          <div
            key={room.id}
            className={`flex-1 min-w-0 py-2 px-1 text-center ${
              index > 0 ? "border-l border-border/30" : ""
            }`}
          >
            <div
              className="inline-block px-2 md:px-3 py-1 rounded text-white text-xs md:text-sm font-medium"
              style={{ backgroundColor: room.color }}
            >
              {room.displayName}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
